import { useEffect, useState } from "react";

import { Activity, Building2, ClipboardList, Copy, Eye, Filter, MapPin, Package2, Plus, UserCheck, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import ActionMenu from "../../../components/shared/ActionMenu";
import CurrencyDisplay from "../../../components/shared/CurrencyDisplay";
import DataTable from "../../../components/shared/DataTable";
import StatusBadge from "../../../components/shared/StatusBadge";
import TruncatedText from "../../../components/shared/TruncatedText";
import { Progress } from "../../../components/ui/progress.jsx";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";
import { formatDate } from "../../../utils/dateUtils";

const STATUS_PROGRESS_WEIGHTS = {
  PENDING: 0,
  PLANNING: 0,
  IN_PROGRESS: 50,
  OVERDUE: 50,
  COMPLETED: 100,
  CANCELLED: 0
};

const calculateCompletionFromStatusCounts = (statusCounts = {}) => {
  const entries = Object.entries(statusCounts);
  if (!entries.length) {
    return 0;
  }

  const totalTasks = entries.reduce((sum, [, count]) => sum + Number(count || 0), 0);
  if (!totalTasks) {
    return 0;
  }

  const weightedProgress = entries.reduce((sum, [status, count]) => {
    const taskCount = Number(count || 0);
    const weight = STATUS_PROGRESS_WEIGHTS[status] ?? 0;
    return sum + taskCount * weight;
  }, 0);

  return Math.round(weightedProgress / totalTasks);
};

const ProjectTable = ({
  projects,
  onEdit,
  onDuplicate,
  deleteProject,
  createDefaultMilestones,
  filters,
  onFilterChange,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setCurrentPage
}) => {
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useAuth();
  const [completionByProjectId, setCompletionByProjectId] = useState({});

  useEffect(() => {
    let isCancelled = false;

    const loadProjectCompletions = async () => {
      if (!projects?.length) {
        if (!isCancelled) {
          setCompletionByProjectId({});
        }
        return;
      }

      const completionEntries = await Promise.all(
        projects.map(async (project) => {
          try {
            const response = await api.get("/tasks", {
              params: {
                projectId: project.id,
                page: 0,
                size: 1
              }
            });
            const statusCounts = response?.data?.metadata?.statusCounts || {};
            return [project.id, calculateCompletionFromStatusCounts(statusCounts)];
          } catch {
            return [project.id, 0];
          }
        })
      );

      if (!isCancelled) {
        setCompletionByProjectId(Object.fromEntries(completionEntries));
      }
    };

    loadProjectCompletions();

    return () => {
      isCancelled = true;
    };
  }, [api, projects]);

  const viewProjectTasks = (projectId) => {
    navigate(`/tasks?projectId=${projectId}`);
  };

  const viewProjectActivities = (projectId) => {
    navigate(`/activities?projectId=${projectId}`);
  };

  const viewProjectMaterials = (projectId) => {
    navigate(`/materials?projectId=${projectId}`);
  };

  const viewProjectAttendance = (projectId) => {
    navigate(`/attendance?projectId=${projectId}`);
  };

  const projectColumns = [
    {
      header: "Project Name",
      accessor: "name",
      isPrimary: true,
      wrap: true,
      render: (val, project) => (
        <div className="md:max-w-[250px]">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <div
              className="md:text-sm md:font-semibold text-yale-blue-900 dark:text-slate-100 cursor-pointer hover:text-yale-blue-600 transition-colors whitespace-normal break-words"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${project.id}`);
              }}
            >
              {val}
            </div>
            {project.deleted && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                Deleted
              </span>
            )}
          </div>
          <div className="md:hidden mt-1 text-xs text-slate-500 flex items-center gap-2">
            <span>ID: {project.id?.substring(0, 8)}...</span>
            <StatusBadge status={project.status} className="scale-75 origin-left" />
          </div>
          {project.description && (
            <TruncatedText
              text={project.description}
              maxLength={45}
              className="text-xs text-slate-500 dark:text-slate-400 mt-0.5"
            />
          )}
        </div>
      )
    },
    {
      header: "Location",
      accessor: "location",
      render: (val) => (
        <div className="flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          {val}
        </div>
      )
    },
    {
      header: "Manager",
      accessor: "projectManager",
      render: (val) => (
        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {val?.name || <span className="text-slate-400 italic text-xs font-normal">Unassigned</span>}
        </div>
      )
    },
    {
      header: "Budget",
      accessor: "estimatedBudget",
      cellClassName: "text-sm font-bold text-yale-blue-900 dark:text-slate-100",
      render: (val) =>
        val !== undefined && val !== null ? (
          <CurrencyDisplay amount={val} />
        ) : (
          <span className="text-slate-400 font-normal italic text-xs">N/A</span>
        )
    },
    {
      header: "Start Date",
      accessor: "startDate",
      sortable: true,
      sortKey: "startDate",
      render: (val) => formatDate(val)
    },
    {
      header: "Status",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters?.status && filters.status !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Status</p>
              <Select value={filters?.status || "ALL"} onValueChange={(val) => onFilterChange("status", val)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "status",
      render: (val) => <StatusBadge status={val} />
    },
    {
      header: "% Completion",
      accessor: "completion",
      render: (_, project) => {
        const completion = completionByProjectId[project.id] ?? 0;

        return (
          <div className="w-28">
            <div className="text-xs text-slate-600 dark:text-slate-300 mb-1">{completion}%</div>
            <Progress value={completion} className="h-1.5" />
          </div>
        );
      }
    },
    {
      header: "Actions",
      filter: user?.permissions?.project?.delete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setCurrentPage(0);
                }}
                id="show-deleted-projects-table"
              />
              <Label htmlFor="show-deleted-projects-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, project) => (
        <ActionMenu
          canEdit={user?.permissions?.project?.update && !project.deleted}
          canDelete={user?.permissions?.project?.delete && !project.deleted}
          onEdit={() => onEdit(project)}
          onDelete={() => deleteProject(project.id)}
          menuWidth="w-56"
          customActions={
            <>
              <DropdownMenuItem
                onClick={() => navigate(`/projects/${project.id}`)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Eye className="w-4 h-4 text-yale-blue-500" />
                <span>View Details</span>
              </DropdownMenuItem>
              {user?.permissions?.project?.create && (
                <DropdownMenuItem onClick={() => onDuplicate(project)} className="flex items-center gap-2 cursor-pointer">
                  <Copy className="w-4 h-4 text-primary" />
                  <span>Duplicate</span>
                </DropdownMenuItem>
              )}
            </>
          }
          quickLinks={
            <>
              <DropdownMenuItem onClick={() => viewProjectTasks(project.id)} className="flex items-center gap-2 cursor-pointer">
                <ClipboardList className="w-4 h-4 text-slate-500" />
                <span>Tasks</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => viewProjectActivities(project.id)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Activity className="w-4 h-4 text-slate-500" />
                <span>Activities</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => viewProjectMaterials(project.id)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Package2 className="w-4 h-4 text-slate-500" />
                <span>Materials</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => viewProjectAttendance(project.id)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <UserCheck className="w-4 h-4 text-slate-500" />
                <span>Attendance</span>
              </DropdownMenuItem>

              {user?.permissions?.project?.update && (
                <DropdownMenuItem
                  onClick={() => createDefaultMilestones(project.id)}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Plus className="w-4 h-4 text-slate-500" />
                  <span>Milestones</span>
                </DropdownMenuItem>
              )}
            </>
          }
        />
      )
    }
  ];

  return (
    <DataTable
      columns={projectColumns}
      data={projects}
      emptyStateTitle="No projects found"
      emptyStateDescription="Get started by creating your first construction project."
      emptyStateIcon={Building2}
      onRowClick={(project) => navigate(`/projects/${project.id}`)}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default ProjectTable;
